import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, differenceInDays, subDays } from 'date-fns';
import { useDateRangeSales } from '../hooks/useSalesData';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EnhancedLocationSelector from '../components/EnhancedLocationSelector';
import ProductMix from '../components/ProductMix';
import PaymentMethods from '../components/PaymentMethods';
import EmployeePerformance from '../components/EmployeePerformance';
import { glassStyle } from '../theme/luxuryTheme';

const DateRangeReport: React.FC = () => {
  const theme = useTheme();
  const [startDate, setStartDate] = useState<Date | null>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date | null>(subDays(new Date(), 1));
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : '';
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : '';
  
  // For multiple stores, join them with comma or use 'all' if none selected
  const storeParam = selectedLocations.length === 0 ? undefined : 
                     selectedLocations.length === 1 ? selectedLocations[0] :
                     selectedLocations.join(',');
  
  const { data, isLoading } = useDateRangeSales(formattedStartDate, formattedEndDate, storeParam);

  const daysDifference = startDate && endDate ? differenceInDays(endDate, startDate) + 1 : 0;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Date Range Report
        </Typography>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          disabled={!data || isLoading}
        >
          Export Report
        </Button>
      </Box>

      {/* Date Selection */}
      <Paper sx={{ ...glassStyle, p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="MM/dd/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="MM/dd/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body1" color="text.secondary">
              {daysDifference} days selected
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <EnhancedLocationSelector
              value={selectedLocations}
              onChange={(value) => setSelectedLocations(Array.isArray(value) ? value : [value])}
              multiple={true}
              label="Select Stores (leave empty for all)"
              showSearch={true}
              showBrandFilter={true}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={glassStyle}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Revenue
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                ${data?.summary?.totalSales?.toLocaleString() || '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={glassStyle}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Orders
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {data?.summary?.totalOrders?.toLocaleString() || '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={glassStyle}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Daily Average
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                ${data?.summary?.averageDailySales ? data.summary.averageDailySales.toFixed(0) : '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={glassStyle}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Average Order
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                ${data?.summary?.averageOrderValue ? data.summary.averageOrderValue.toFixed(2) : '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sales Chart */}
      {data?.dailyBreakdown && data.dailyBreakdown.length > 0 && (
        <Paper sx={{ ...glassStyle, p: 3, height: 400, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Daily Sales Trend
          </Typography>
          <ResponsiveContainer width="100%" height="90%">
            <LineChart data={data.dailyBreakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                stroke="#A0AEC0"
                style={{ fontSize: '0.75rem' }}
              />
              <YAxis 
                stroke="#A0AEC0"
                style={{ fontSize: '0.75rem' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right"
                stroke="#A0AEC0"
                style={{ fontSize: '0.75rem' }}
              />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  if (name === 'Sales') return [`$${value.toLocaleString()}`, name];
                  return [value.toLocaleString(), name];
                }}
                labelFormatter={(label) => format(new Date(label), 'MMM dd, yyyy')}
                contentStyle={{
                  backgroundColor: 'rgba(28, 24, 25, 0.95)',
                  border: '1px solid rgba(175, 35, 38, 0.3)',
                  borderRadius: '8px',
                }}
              />
              <Legend 
                wrapperStyle={{ color: '#A0AEC0' }}
              />
              <Line 
                type="monotone" 
                dataKey="sales" 
                stroke={theme.palette.primary.main} 
                strokeWidth={3}
                name="Sales"
                dot={{ fill: theme.palette.primary.main, r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="orders" 
                stroke={theme.palette.secondary.main} 
                strokeWidth={3}
                name="Orders"
                yAxisId="right"
                dot={{ fill: theme.palette.secondary.main, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* New Analytics Components */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductMix date={formattedEndDate} storeId={storeParam} />
        </Grid>
        <Grid item xs={12} md={4}>
          <PaymentMethods date={formattedEndDate} storeId={storeParam} />
        </Grid>
        <Grid item xs={12} md={4}>
          <EmployeePerformance date={formattedEndDate} storeId={storeParam} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DateRangeReport;